import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import { TreePine, Fish, Leaf, Moon, Star, Sparkles } from 'lucide-react';

const HiddenShop = () => {
  const [timeOfDay, setTimeOfDay] = useState('day');
  const [particles, setParticles] = useState([]);

  // Generate floating particles
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 20; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 4 + 2,
          speed: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.6 + 0.2,
          type: Math.random() > 0.5 ? 'leaf' : 'sparkle'
        });
      }
      setParticles(newParticles);
    };

    generateParticles();
    const interval = setInterval(generateParticles, 5000);
    return () => clearInterval(interval);
  }, []);

  // Determine time of day based on current hour
  useEffect(() => {
    const hour = new Date().getHours();
    setTimeOfDay(hour >= 6 && hour < 18 ? 'day' : 'night');
  }, []);

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Dark Forest Background */}
      <div className="fixed inset-0 -z-10">
        {/* Dark forest background image */}
        <img
          src="https://wallpapers.com/images/hd/dark-forest-background-47a32hde7afgsxxp.jpg"
          alt="Dark Forest Background"
          className="w-full h-full object-cover"
        />
        
        {/* Dark magical overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-purple-900/50 to-black/80" />
        
        {/* Mystical floating orbs */}
        <div className="absolute top-20 left-10 w-6 h-6 bg-purple-500 rounded-full opacity-60 animate-bounce shadow-lg shadow-purple-500/50" />
        <div className="absolute top-32 right-20 w-4 h-4 bg-blue-400 rounded-full opacity-70 animate-pulse shadow-lg shadow-blue-400/50" />
        <div className="absolute top-40 left-1/3 w-5 h-5 bg-indigo-400 rounded-full opacity-50 animate-ping shadow-lg shadow-indigo-400/50" />
        <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-pink-400 rounded-full opacity-60 animate-bounce shadow-lg shadow-pink-400/50" style={{ animationDelay: '1s' }} />
        
        {/* Magical sparkles */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 25 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-purple-300 rounded-full opacity-80 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            ></div>
          ))}
        </div>

        {/* Mystical energy waves */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-blue-300 rounded-full opacity-40 animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 8}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }}
            ></div>
          ))}
        </div>
        
        {/* Floating mystical particles */}
        {particles.map(particle => (
          <div
            key={particle.id}
            className={`absolute transition-all duration-1000 ${
              particle.type === 'leaf' ? 'text-green-300' : 'text-purple-300'
            }`}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              fontSize: `${particle.size}px`,
              opacity: particle.opacity,
              animation: `float ${particle.speed}s ease-in-out infinite`,
              filter: 'drop-shadow(0 0 4px rgba(147, 51, 234, 0.5))'
            }}
          >
            {particle.type === 'leaf' ? '🍃' : '✨'}
          </div>
        ))}
      </div>

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Gone Fishing Sign */}
          <div className="relative mb-12">
            {/* Sign Post */}
            <div className="w-2 h-32 bg-gray-800 mx-auto mb-4 rounded-full shadow-lg shadow-gray-900/50" />
            
            {/* Sign Board */}
            <div className="relative bg-gray-900 border-4 border-purple-600 rounded-lg p-8 shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-300">
              {/* Magical nail holes */}
              <div className="absolute top-2 left-2 w-2 h-2 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50" />
              <div className="absolute top-2 right-2 w-2 h-2 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50" />
              <div className="absolute bottom-2 left-2 w-2 h-2 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50" />
              <div className="absolute bottom-2 right-2 w-2 h-2 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50" />
              
              {/* Sign Text */}
              <div className="relative z-10">
                <h1 className="text-6xl font-bold text-purple-300 mb-4 font-serif drop-shadow-lg shadow-purple-500/50">
                  Gone Fishing
                </h1>
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <Fish className="text-4xl text-blue-400 animate-bounce drop-shadow-lg shadow-blue-400/50" />
                  <span className="text-2xl text-purple-300 drop-shadow-lg shadow-purple-500/50">🐟</span>
                  <Fish className="text-4xl text-blue-400 animate-bounce drop-shadow-lg shadow-blue-400/50" style={{ animationDelay: '0.5s' }} />
                </div>
                <p className="text-xl text-purple-200 font-medium drop-shadow-lg shadow-purple-500/50">
                  The shopkeeper has wandered off into the mystical forest...
                </p>
              </div>
            </div>
          </div>


          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/" className="bg-gray-800 hover:bg-gray-700 border-2 border-purple-500 hover:border-purple-400 text-purple-300 hover:text-purple-200 px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg shadow-purple-500/25 hover:shadow-purple-400/50">
              Return to Town
            </Link>
            <Link to="/events" className="bg-gray-800 hover:bg-gray-700 border-2 border-blue-500 hover:border-blue-400 text-blue-300 hover:text-blue-200 px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-400/50">
              Check Events
            </Link>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-gray-800 hover:bg-gray-700 border-2 border-indigo-500 hover:border-indigo-400 text-indigo-300 hover:text-indigo-200 px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-400/50"
            >
              Wait Here
            </button>
          </div>
        </div>
      </main>

      {/* Dark Forest Footer */}
      <footer className="bg-black bg-opacity-70 border-t border-purple-600 py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="text-purple-200 font-bold drop-shadow-lg shadow-purple-500/50">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
          <p className="text-purple-300 text-sm mt-2 italic drop-shadow-lg shadow-purple-500/50">
            The forest remembers all who wander here...
          </p>
        </div>
      </footer>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        .animate-twinkle {
          animation: twinkle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HiddenShop;

